import Vue from 'vue';
import { DEFAULT_INTERFACE_PREFERENCES } from '@/shells/models/interfacePreferences';
import { interfacePreferencesRepository } from '@/shells/api/interfacePreferencesRepository';

const CACHE_PREFIX = 'eurekers.interface-preferences.v1.';

function safeCachedPreferences(storage, uid) {
  try {
    const value = JSON.parse(storage.getItem(`${CACHE_PREFIX}${uid}`));
    if (!value || !['legacy', 'modern'].includes(value.shell) || !['light', 'dark', 'system'].includes(value.themeMode)) return null;
    return value;
  } catch (error) {
    return null;
  }
}

export class InterfacePreferencesController {
  constructor(repository = interfacePreferencesRepository, storage = window.localStorage) {
    this.repository = repository;
    this.storage = storage;
    this.entries = new Map();
    this.pending = new Map();
  }

  stateFor(uid) {
    const key = String(uid || 0);
    if (!this.entries.has(key)) {
      this.entries.set(key, Vue.observable({
        status: 'idle',
        error: null,
        preferences: safeCachedPreferences(this.storage, key) || { ...DEFAULT_INTERFACE_PREFERENCES }
      }));
    }
    return this.entries.get(key);
  }

  async load(uid, { force = false } = {}) {
    const key = String(uid || 0);
    const state = this.stateFor(key);
    if (!force && state.status === 'ready') return state.preferences;
    if (this.pending.has(key)) return this.pending.get(key);

    state.status = 'loading';
    state.error = null;
    const request = this.repository.get()
      .then(preferences => {
        state.preferences = preferences;
        state.status = 'ready';
        this.storage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(preferences));
        return preferences;
      })
      .catch(error => {
        state.status = 'error';
        state.error = error;
        throw error;
      })
      .finally(() => this.pending.delete(key));

    this.pending.set(key, request);
    return request;
  }

  async update(uid, patch) {
    const key = String(uid || 0);
    const state = this.stateFor(key);
    const previous = { ...state.preferences };
    state.preferences = { ...state.preferences, ...patch };
    state.error = null;

    try {
      const preferences = await this.repository.update(patch);
      state.preferences = preferences;
      state.status = 'ready';
      this.storage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(preferences));
      return preferences;
    } catch (error) {
      state.preferences = previous;
      state.status = 'error';
      state.error = error;
      throw error;
    }
  }
}

export const interfacePreferencesController = new InterfacePreferencesController();
